"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import {
	Delegation,
	DelegationChain,
	DelegationIdentity,
	Ed25519KeyIdentity,
} from "@icp-sdk/core/identity";
import { CheckCircle2, Loader2 } from "lucide-react";
import Image from "next/image";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/hooks/useLanguage";
import type { SignMessageType } from "@/lib/idl/odin_siwb_idl";
import {
	connectBitcoinWallet,
	getOkxBitcoinProvider,
	OKX_CHROME_EXTENSION_URL,
	waitForOkxBitcoinProvider,
	WalletFactory,
} from "@/lib/services/btc-wallet-providers";
import {
	buildSiwbSignMessageType,
	normalizeBitcoinPublicKey,
	resolveBitcoinSigningProfile,
	signBitcoinMessageWithFallback,
} from "@/lib/services/btc-signing-profile";
import {
	getOkxConnectClient,
	prewarmOkxConnect,
} from "@/lib/services/okx-connect";
import {
	buildWalletLoginIntentUrl,
	clearStoredWalletLoginIntent,
	consumeWalletLoginIntentFromUrl,
	createWalletLoginIntent,
	getStoredWalletLoginIntent,
	persistWalletLoginIntent,
} from "@/lib/services/mobile-wallet-login-intent";
import { OdinFunAuthService } from "@/lib/services/odin-fun-auth";
import { siwbAdapter } from "@/lib/services/siwb-adapter";
import { translations } from "@/lib/translations";
import {
	getMobileWalletEnvironment,
	handleMobileWalletConnection,
	isBinanceBitcoinSupported,
	isBinanceExtensionInstalled,
	isMobileDevice,
} from "@/lib/utils/mobile-detection";
import { SessionKeyEncryption } from "@/lib/utils/session-key-encryption";

const uiLog = createLogAdapter("components/auth/sign-in-modal.tsx");

type LoginMetrics = {
	walletType: string;
	mark: (stage: string, data?: Record<string, unknown>) => void;
};

function createLoginMetrics(walletType: string): LoginMetrics {
	const startedAt = performance.now();

	return {
		walletType,
		mark: (stage, data = {}) => {
			uiLog.info(`⏱️ [${walletType}] ${stage}`, {
				durationMs: Math.round(performance.now() - startedAt),
				...data,
			});
		},
	};
}

function isOkxConnectedAddressMismatchError(error: unknown): boolean {
	if (!error || typeof error !== "object") {
		return false;
	}

	const candidateStrings = [
		(error as { message?: unknown }).message,
		(error as { code?: unknown }).code,
		(error as { stack?: unknown }).stack,
		(error as { data?: { message?: unknown } }).data?.message,
	];

	const normalized = candidateStrings
		.map((value) => (typeof value === "string" ? value.toLowerCase() : String(value)))
		.join(" ");

	return (
		normalized.includes("invalid parameters") &&
		normalized.includes("legal address")
	);
}

// Telegram login disabled for web - only wallet login allowed

interface SignInModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: (
		principal: string,
		address: string,
		identity: DelegationIdentity,
	) => void;
}

export default function SignInModal({
	open,
	onOpenChange,
	onSuccess,
}: SignInModalProps) {
	const [isLoading, setIsLoading] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [authStatus, setAuthStatus] = useState<string | null>(null);
	const [loginSuccess, setLoginSuccess] = useState(false);
	const { language } = useLanguage();
	const t: any = translations[language] || translations.en;
	const cleanupRef = React.useRef<(() => void) | null>(null);
	const refreshWalletAvailabilityRef = React.useRef<(() => Promise<void>) | null>(
		null,
	);
	const autoStartedWalletRef = React.useRef<string | null>(null);
	const autoOpenedFromIntentRef = React.useRef(false);
	const autoResumeWalletLoginRef = React.useRef<
		((walletType: string) => void) | null
	>(null);

	// 本地化文本
	const texts = {
		title:
			t.auth?.signIn ||
			(language === "zh" ? "选择登录方式" : "Choose Login Method"),
		description:
			t.auth?.selectWallet ||
			(language === "zh"
				? "选择您的钱包进行登录"
				: "Select your wallet to login"),
		cancel: t.common?.cancel || (language === "zh" ? "取消" : "Cancel"),
		btcWallets:
			t.auth?.btcWallets ||
			(language === "zh" ? "比特币钱包" : "Bitcoin Wallets"),
		connecting:
			t.auth?.connecting ||
			(language === "zh" ? "正在连接钱包..." : "Connecting wallet..."),
		signing:
			t.auth?.signing || (language === "zh" ? "正在签名..." : "Signing..."),
		verifying:
			t.auth?.verifying || (language === "zh" ? "正在验证..." : "Verifying..."),
		authorizing:
			t.auth?.authorizing ||
			(language === "zh" ? "正在获取授权..." : "Getting authorization..."),
		success:
			t.auth?.success ||
			(language === "zh" ? "登录成功！" : "Login successful!"),
		closing:
			t.auth?.closing || (language === "zh" ? "即将关闭..." : "Closing..."),
		agreement:
			language === "zh"
				? "登录即表示您同意我们的服务条款和隐私政策"
				: "By signing in, you agree to our Terms of Service and Privacy Policy",
		okxInstallTitle:
			language === "zh"
				? "未检测到 OKX Chrome 插件"
				: "OKX Chrome extension not detected",
		okxInstallDescription:
			language === "zh"
				? "请先安装并解锁 OKX Wallet Chrome 插件，安装完成后刷新当前页面再继续登录。"
				: "Install and unlock the OKX Wallet Chrome extension, then refresh this page before signing in again.",
		okxInstallButton:
			language === "zh"
				? "安装 OKX Chrome 插件"
				: "Install OKX Chrome Extension",
		recheckWallets:
			language === "zh" ? "重新检测钱包" : "Recheck wallets",
	};

	// 支持的BTC钱包列表
	const supportedWallets = React.useMemo(
		() => [
			{ id: "okx", name: "OKX Wallet", icon: "/wallet/okx.svg" },
			{ id: "binance", name: "Binance Wallet", icon: "/wallet/binance.svg" },
			{ id: "unisat", name: "Unisat Wallet", icon: "/wallet/unisat.svg" },
			{ id: "xverse", name: "Xverse Wallet", icon: "/wallet/xverse.svg" },
			{ id: "phantom", name: "Phantom Wallet", icon: "/wallet/phantom.svg" },
			{ id: "tokenpocket", name: "TokenPocket Wallet", icon: "/wallet/TP.svg" },
		],
		[],
	);

	// 获取可用钱包
	const [availableWallets, setAvailableWallets] = useState<string[]>([]);

	refreshWalletAvailabilityRef.current = async () => {
		const mobileEnv = getMobileWalletEnvironment();

		uiLog.info("🔍 钱包可用性检测:", {
			isMobile: mobileEnv.isMobile,
			isInWalletBrowser: mobileEnv.isInWalletBrowser,
			walletType: mobileEnv.walletType,
			supportedWallets: mobileEnv.supportedWallets,
		});

		if (mobileEnv.isMobile) {
			setAvailableWallets(supportedWallets.map((w) => w.id));
			uiLog.info(
				"📱 移动端：显示所有支持的钱包",
				supportedWallets.map((w) => w.id),
			);
			return;
		}

		const okxProvider = await waitForOkxBitcoinProvider({
			timeoutMs: 1200,
			intervalMs: 150,
		});
		const available = WalletFactory.getAvailableWallets().map((w) => w.id);
		if (okxProvider && !available.includes("okx")) {
			available.unshift("okx");
		}

		setAvailableWallets(Array.from(new Set(available)));
		uiLog.info("💻 PC端：可用钱包", available);
	};

	React.useEffect(() => {
		const intentFromUrl = consumeWalletLoginIntentFromUrl();
		if (!intentFromUrl || autoOpenedFromIntentRef.current) {
			return;
		}

		persistWalletLoginIntent(intentFromUrl);
		autoOpenedFromIntentRef.current = true;
		onOpenChange(true);
	}, [onOpenChange]);

	React.useEffect(() => {
		if (!open) return;

		let okxAccountsChangedHandler: (() => void) | null = null;
		const timers: Array<ReturnType<typeof setTimeout>> = [];

		const refreshWallets = () => {
			void refreshWalletAvailabilityRef.current?.();
		};

		refreshWallets();
		[300, 800, 1500].forEach((delay) => {
			timers.push(setTimeout(refreshWallets, delay));
		});

		const handleFocus = () => {
			refreshWallets();
		};

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				refreshWallets();
			}
		};

		window.addEventListener("focus", handleFocus);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		const okxProvider = getOkxBitcoinProvider();
		if (okxProvider?.on) {
			okxAccountsChangedHandler = () => refreshWallets();
			okxProvider.on("accountsChanged", okxAccountsChangedHandler);
		}

		return () => {
			for (const timer of timers) {
				clearTimeout(timer);
			}
			window.removeEventListener("focus", handleFocus);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
			if (okxProvider && okxAccountsChangedHandler) {
				okxProvider.off?.("accountsChanged", okxAccountsChangedHandler);
				okxProvider.removeListener?.(
					"accountsChanged",
					okxAccountsChangedHandler,
				);
			}
		};
	}, [open, supportedWallets]);

	React.useEffect(() => {
		if (!open) return;

		const mobileEnv = getMobileWalletEnvironment();
		if (!mobileEnv.isMobile || mobileEnv.walletType === "okx") {
			return;
		}

		prewarmOkxConnect({ language });
	}, [language, open]);

	React.useEffect(() => {
		if (!open || isLoading || loginSuccess) {
			return;
		}

		const intent = getStoredWalletLoginIntent();
		if (!intent || !intent.autoStartLogin) {
			return;
		}

		const mobileEnv = getMobileWalletEnvironment();
		if (
			intent.walletType !== "tokenpocket" ||
			mobileEnv.walletType !== "tokenpocket" ||
			autoStartedWalletRef.current === intent.walletType
		) {
			return;
		}

		autoStartedWalletRef.current = intent.walletType;
		autoResumeWalletLoginRef.current?.(intent.walletType);
	}, [isLoading, loginSuccess, open]);

	// Telegram login is disabled for web - only available through account linking after wallet login

	const finalizeSiwbLogin = async ({
		walletType,
		address,
		signature,
		actualPublicKey,
		sessionIdentity,
		sessionKey,
		signMessageType,
		metrics,
	}: {
		walletType: string;
		address: string;
		signature: string;
		actualPublicKey: string;
		sessionIdentity: Ed25519KeyIdentity;
		sessionKey: Uint8Array;
		signMessageType: SignMessageType;
		metrics?: LoginMetrics;
	}) => {
		setAuthStatus(texts.verifying);

		// 使用适配器执行登录
		const loginResponse = await siwbAdapter.login(
			signature,
			address,
			actualPublicKey,
			sessionKey,
			signMessageType,
		);

		if ("Err" in loginResponse) {
			throw new Error(
				language === "zh"
					? `登录失败: ${loginResponse.Err}`
					: `Login failed: ${loginResponse.Err}`,
			);
		}
		metrics?.mark("siwb_login_completed");

		// 从登录响应中获取信息
		const loginDetails = loginResponse.Ok;

		// 使用适配器获取委托
		const delegationResponse = await siwbAdapter.getDelegation(
			address,
			sessionKey,
			loginDetails.expiration,
		);

		if ("Err" in delegationResponse) {
			throw new Error(
				language === "zh"
					? `获取委托失败: ${delegationResponse.Err}`
					: `Failed to get delegation: ${delegationResponse.Err}`,
			);
		}
		metrics?.mark("siwb_get_delegation_completed");

		// 根据委托链创建最终身份
		const delegationResult = delegationResponse.Ok;
		const userCanisterPubkeyDer = new Uint8Array(
			loginDetails.user_canister_pubkey,
		);

		// 根据 get_delegation 结果创建委托链
		function createDelegationChainFromGetResult(
			getResult: any,
			middlePubkeyDer: Uint8Array,
		) {
			const delegations = [
				{
					delegation: new Delegation(
						getResult.delegation.pubkey,
						getResult.delegation.expiration,
						getResult.delegation.targets
							? getResult.delegation.targets[0]
							: undefined,
					),
					signature: getResult.signature,
				},
			];
			// 使用传入的 middlePubkeyDer 构建委托链
			return DelegationChain.fromDelegations(delegations, middlePubkeyDer);
		}

		const delegationChain = createDelegationChainFromGetResult(
			delegationResult,
			userCanisterPubkeyDer,
		);
		const finalIdentity = DelegationIdentity.fromDelegation(
			sessionIdentity,
			delegationChain,
		);

		// 获取 Principal
		const principal = finalIdentity.getPrincipal().toString();

		// 保存到本地存储
		localStorage.setItem("siwb_authenticated", "true");
		localStorage.setItem("user_btc_address", address);
		localStorage.setItem("user_principal", principal);
		localStorage.setItem("user_wallet_type", walletType); // 保存钱包类型
		// 保存公钥用于后续 Fomowell 关联等操作
		if (actualPublicKey) {
			localStorage.setItem("user_btc_publicKey", actualPublicKey);
		}

		// 保存重建身份所需的数据，使用与 siwb.ts 一致的数据结构
		localStorage.setItem(
			"user_session_key",
			Buffer.from(sessionKey).toString("hex"),
		);
		// 使用加密存储会话私钥
		await SessionKeyEncryption.encryptSessionKey(
			Buffer.from(sessionIdentity.getKeyPair().secretKey).toString("hex"),
			principal,
		);

		// 构建与 siwb.ts 一致的委托数据结构
		const delegationWithMeta = {
			delegationData: delegationResult,
			userCanisterPubkey: Array.from(userCanisterPubkeyDer), // 保存为数组便于序列化
		};

		// 使用自定义序列化函数处理 BigInt
		localStorage.setItem(
			"user_delegation_result",
			JSON.stringify(delegationWithMeta, (key, value) =>
				typeof value === "bigint" ? value.toString() : value,
			),
		);

		// 显示成功状态
		setAuthStatus(texts.success);
		setLoginSuccess(true);
		setError(null);
		if (walletType === "tokenpocket") {
			clearStoredWalletLoginIntent();
			autoStartedWalletRef.current = null;
		}
		metrics?.mark("login_ui_completed", { principal });

			try {
				await OdinFunAuthService.getOdinFunAuth(finalIdentity);
				metrics?.mark("odin_fun_auth_completed");
			} catch (odinFunError: any) {
				uiLog.warn("odin.fun 授权失败，但不影响主登录流程", {
					walletType,
					error: odinFunError,
				});
			}

			// 通知父组件登录成功
			if (onSuccess) {
				onSuccess(principal, address, finalIdentity);
			}

			// 触发VIP状态检测（通过Context自动处理）
			uiLog.info("用户登录成功，VIP状态将自动更新");

		// 2秒后关闭弹窗
		setTimeout(() => {
			setAuthStatus(texts.closing);
			setTimeout(() => {
				onOpenChange(false);
			}, 500);
		}, 1500);
	};

	const signInWithOkxConnect = async (metrics: LoginMetrics) => {
		setAuthStatus(
			language === "zh" ? "正在唤起 OKX 钱包..." : "Opening OKX Wallet...",
		);

		const okxClient = await getOkxConnectClient({ language });
		metrics.mark("okx_connect_ready");
		const account = await okxClient.getAccount();
		metrics.mark("okx_account_received", {
			hasPublicKey: Boolean(account.publicKey),
		});

		if (!account.address) {
			throw new Error(
				language === "zh"
					? "未获取到 OKX 钱包地址，请重试。"
					: "Failed to get OKX wallet address, please try again.",
			);
		}

		// 显示当前使用的模式
		uiLog.info(`🔧 SIWB Mode: ${siwbAdapter.getMode()}`);

		// 生成会话密钥对
		const sessionIdentity = Ed25519KeyIdentity.generate();
		const sessionKey = new Uint8Array(sessionIdentity.getPublicKey().toDer());

		setAuthStatus(texts.signing);

		// 使用适配器获取待签名消息
		const prepareResponse = await siwbAdapter.prepareLogin(account.address);
		if ("Err" in prepareResponse) {
			throw new Error(
				language === "zh"
					? `准备登录失败: ${prepareResponse.Err}`
					: `Login preparation failed: ${prepareResponse.Err}`,
			);
		}
		metrics.mark("siwb_prepare_login_completed");

		const messageToSign = prepareResponse.Ok;

		const okxSigningProfile = resolveBitcoinSigningProfile("okx", account.address);
		let signature: string;
		let usedProtocol = okxSigningProfile.primaryProtocol;

		try {
			signature = await okxClient.signMessage(
				messageToSign,
				okxSigningProfile.primaryProtocol,
			);
		} catch (error) {
			if (okxSigningProfile.fallbackProtocols.length === 0) {
				throw error;
			}

			uiLog.warn("OKX Connect signing failed, retrying with fallback protocol", {
				address: account.address,
				addressType: okxSigningProfile.addressType,
				primaryProtocol: okxSigningProfile.primaryProtocol,
				fallbackProtocol: okxSigningProfile.fallbackProtocols[0],
				error,
			});

			usedProtocol = okxSigningProfile.fallbackProtocols[0];
			signature = await okxClient.signMessage(messageToSign, usedProtocol);
		}
		metrics.mark("wallet_sign_completed", {
			signProtocol: usedProtocol,
		});

		// 获取公钥
			let actualPublicKey: string | undefined = account.publicKey;
			if (!actualPublicKey) {
				try {
					const okxwallet = (window as any).okxwallet;
					if (okxwallet?.bitcoin?.getPublicKey) {
						actualPublicKey = await okxwallet.bitcoin.getPublicKey();
				}
			} catch (error) {
				uiLog.warn("Failed to get public key from OKX wallet injection", {
					error,
					});
				}
			}

			if (!actualPublicKey) {
				throw new Error(
					language === "zh"
						? "无法获取 OKX 公钥，请在 OKX 钱包内重试。"
					: "Failed to get OKX public key. Please retry in OKX wallet.",
			);
		}

		const signMessageType = buildSiwbSignMessageType(usedProtocol);

		await finalizeSiwbLogin({
			walletType: "okx",
			address: account.address,
			signature,
			actualPublicKey,
			sessionIdentity,
			sessionKey,
			signMessageType,
			metrics,
		});
	};

	const handleBitcoinWalletSignIn = async (walletType: string) => {
		try {
			const metrics = createLoginMetrics(walletType);
			metrics.mark("login_clicked");
			setIsLoading(walletType);
			setError(null);
			setAuthStatus(texts.connecting);

			// 获取移动端环境信息
			const mobileEnv = getMobileWalletEnvironment();
			uiLog.info("📱 移动端环境检测:", mobileEnv);

			if (walletType === "tokenpocket" && mobileEnv.walletType === "tokenpocket") {
				clearStoredWalletLoginIntent();
			}

			// 移动端 OKX 分流：
			// 1. 已在 OKX 钱包内置浏览器中时，优先走注入式 provider，避免再套一层 Connect UI
			// 2. 仅在外部浏览器/PWA 场景使用 OKX Connect
			if (
				walletType === "okx" &&
				mobileEnv.isMobile &&
				mobileEnv.walletType !== "okx"
			) {
				await signInWithOkxConnect(metrics);
				return;
			}

			if (walletType === "okx" && !mobileEnv.isMobile) {
				const okxProvider = await waitForOkxBitcoinProvider({
					timeoutMs: 1800,
					intervalMs: 150,
				});
				if (!okxProvider) {
					setError(
						language === "zh"
							? `未检测到 OKX Wallet Chrome 插件。请先安装插件并刷新页面：${OKX_CHROME_EXTENSION_URL}`
							: `OKX Wallet Chrome extension not detected. Install it and refresh the page: ${OKX_CHROME_EXTENSION_URL}`,
					);
					setAuthStatus(null);
					return;
				}
			}

			// 添加版本标识确保代码是最新的
			const SIGNIN_VERSION = "SignIn-v2024.12.24-14:45";
			uiLog.info("🚀 登录组件版本:", SIGNIN_VERSION);

			// 移动端处理：如果不在对应钱包浏览器中且没有对应的注入对象，显示深链接提示
			uiLog.info("🔍 移动端环境判断:", {
				isMobile: mobileEnv.isMobile,
				mobileWalletType: mobileEnv.walletType,
				clickedWalletType: walletType,
				condition: mobileEnv.isMobile && mobileEnv.walletType !== walletType,
			});

			if (mobileEnv.isMobile && mobileEnv.walletType !== walletType) {
				const wallet = WalletFactory.getWallet(walletType);
				const canConnect = wallet.isAvailable();

				uiLog.info(`🔍 钱包 ${walletType} 检测结果:`, {
					walletType,
					mobileWalletType: mobileEnv.walletType,
					canConnect,
					userAgent: navigator.userAgent,
				});

				if (!canConnect) {
					const walletNames = {
						okx: "OKX钱包",
						binance: "Binance钱包",
						unisat: "UniSat钱包",
						xverse: "Xverse钱包",
						phantom: "Phantom钱包",
						tokenpocket: "TokenPocket钱包",
					};
					const walletName =
						walletNames[walletType as keyof typeof walletNames] || walletType;

					setAuthStatus(`正在尝试打开${walletName}...`);

					const deepLinkDappUrl =
						walletType === "tokenpocket"
							? buildWalletLoginIntentUrl("tokenpocket")
							: undefined;
					if (walletType === "tokenpocket") {
						persistWalletLoginIntent(createWalletLoginIntent("tokenpocket"));
					}

					// 尝试深链接跳转
					const success = await handleMobileWalletConnection(
						walletType,
						() => {
							uiLog.info(`✅ 成功跳转到${walletName}`);
							setAuthStatus(`请在${walletName}中完成登录后返回`);
						},
						(errorMessage) => {
							setError(errorMessage);
							setIsLoading(null);
							setAuthStatus(null);
						},
						{
							dappUrl: deepLinkDappUrl,
						},
					);

					if (!success) {
						// 深链接失败，错误已在回调中设置
						return;
					}

					if (walletType === "tokenpocket") {
						setAuthStatus(
							language === "zh"
								? "正在前往 TP 钱包并准备自动登录..."
								: "Opening TokenPocket and preparing auto login...",
						);
						return;
					}

					// 对于 Xverse 钱包，添加页面可见性监听器来检测用户返回
					if (walletType === "xverse") {
						let checkInterval: NodeJS.Timeout;
						let checkCount = 0;
						const maxChecks = 20; // 最多检查20次（20秒）

						const checkWalletAvailable = () => {
							checkCount++;

							// 检查钱包是否现在可用
							const wallet = WalletFactory.getWallet(walletType);
							if (
								wallet.isAvailable() ||
								(window as any).XverseProviders?.BitcoinProvider
							) {
								uiLog.info(`✅ 检测到${walletName}已可用，继续登录流程`);
								clearInterval(checkInterval);

								// 清除错误状态
								setError(null);
								setAuthStatus(texts.connecting);

								// 继续执行登录流程（直接从连接钱包步骤开始）
								(async () => {
									try {
										// 连接钱包
										const connectionResult =
											await connectBitcoinWallet(walletType);
										const { address, publicKey } = connectionResult;

										if (!address) {
											setError(
												language === "zh"
													? `未找到${walletType}钱包地址，请先连接钱包。`
													: `${walletType} wallet address not found, please connect wallet first.`,
											);
											setIsLoading(null);
											setAuthStatus(null);
											return;
										}

										// 显示当前使用的模式
										uiLog.info(`🔧 SIWB Mode: ${siwbAdapter.getMode()}`);

										// 生成会话密钥对
										const sessionIdentity = Ed25519KeyIdentity.generate();
										const sessionKey = new Uint8Array(
											sessionIdentity.getPublicKey().toDer(),
										);

										setAuthStatus(texts.signing);

										// 使用适配器获取待签名消息
										const prepareResponse =
											await siwbAdapter.prepareLogin(address);
										if ("Err" in prepareResponse) {
											throw new Error(
												language === "zh"
													? `准备登录失败: ${prepareResponse.Err}`
													: `Login preparation failed: ${prepareResponse.Err}`,
											);
										}

										const messageToSign = prepareResponse.Ok;

										// 使用钱包签名
										const wallet = WalletFactory.getWallet(walletType);
										if (!wallet) {
											throw new Error(
												language === "zh"
													? `钱包 ${walletType} 不可用`
													: `Wallet ${walletType} not available`,
											);
										}

										// 继续原有的签名和登录流程...
										const signature = await wallet.signMessage(
											messageToSign,
											address,
										);

										// 获取公钥
										let actualPublicKey: string | undefined = publicKey;
										if (walletType === "xverse") {
											const xverseWallet = wallet as any;
											if (xverseWallet.getPublicKey) {
												actualPublicKey = await xverseWallet.getPublicKey();
											}

											if (address.startsWith("bc1p")) {
												if (
													actualPublicKey &&
													actualPublicKey.length === 66 &&
													(actualPublicKey.startsWith("02") ||
														actualPublicKey.startsWith("03"))
												) {
													actualPublicKey = actualPublicKey.slice(2);
												}
											}
										}

										setAuthStatus(texts.verifying);

										// 根据地址类型选择签名验证方式
										let signMessageType;
										if (walletType === "xverse") {
											if (address.startsWith("bc1p")) {
												signMessageType = { Bip322Simple: null };
											} else {
												signMessageType = { ECDSA: null };
											}
										} else {
											signMessageType = { ECDSA: null };
										}

										// 使用适配器执行登录
										const loginResponse = await siwbAdapter.login(
											signature,
											address,
											actualPublicKey,
											sessionKey,
											signMessageType,
										);

										if ("Err" in loginResponse) {
											throw new Error(
												language === "zh"
													? `登录失败: ${loginResponse.Err}`
													: `Login failed: ${loginResponse.Err}`,
											);
										}

										// 继续后续的委托获取和身份创建流程...
										const loginDetails = loginResponse.Ok;

										// 使用适配器获取委托
										const delegationResponse = await siwbAdapter.getDelegation(
											address,
											sessionKey,
											loginDetails.expiration,
										);

										if ("Err" in delegationResponse) {
											throw new Error(
												language === "zh"
													? `获取委托失败: ${delegationResponse.Err}`
													: `Failed to get delegation: ${delegationResponse.Err}`,
											);
										}

										const delegationResult = delegationResponse.Ok;
										const userCanisterPubkeyDer = new Uint8Array(
											loginDetails.user_canister_pubkey,
										);

										function createDelegationChainFromGetResult(
											getResult: any,
											middlePubkeyDer: Uint8Array,
										) {
											const delegations = [
												{
													delegation: new Delegation(
														getResult.delegation.pubkey,
														getResult.delegation.expiration,
														getResult.delegation.targets
															? getResult.delegation.targets[0]
															: undefined,
													),
													signature: getResult.signature,
												},
											];
											return DelegationChain.fromDelegations(
												delegations,
												middlePubkeyDer,
											);
										}

										const delegationChain = createDelegationChainFromGetResult(
											delegationResult,
											userCanisterPubkeyDer,
										);
										const finalIdentity = DelegationIdentity.fromDelegation(
											sessionIdentity,
											delegationChain,
										);

										const principal = finalIdentity.getPrincipal().toString();

										// 保存到本地存储
										localStorage.setItem("siwb_authenticated", "true");
										localStorage.setItem("user_btc_address", address);
										localStorage.setItem("user_principal", principal);
										localStorage.setItem("user_wallet_type", walletType); // 保存钱包类型
										localStorage.setItem(
											"user_session_key",
											Buffer.from(sessionKey).toString("hex"),
										);
										// 保存公钥用于后续 Fomowell 关联等操作
										if (actualPublicKey) {
											localStorage.setItem(
												"user_btc_publicKey",
												actualPublicKey,
											);
										}
										// 使用加密存储会话私钥
										await SessionKeyEncryption.encryptSessionKey(
											Buffer.from(
												sessionIdentity.getKeyPair().secretKey,
											).toString("hex"),
											principal,
										);

										const delegationWithMeta = {
											delegationData: delegationResult,
											userCanisterPubkey: Array.from(userCanisterPubkeyDer),
										};

										localStorage.setItem(
											"user_delegation_result",
											JSON.stringify(delegationWithMeta, (key, value) =>
												typeof value === "bigint" ? value.toString() : value,
											),
										);

										// 显示成功状态
										setAuthStatus(texts.success);
										setLoginSuccess(true);
										setError(null);
										metrics.mark("login_ui_completed", { principal });

											try {
												await OdinFunAuthService.getOdinFunAuth(finalIdentity);
												metrics.mark("odin_fun_auth_completed");
											} catch (odinFunError: any) {
												uiLog.warn("odin.fun 授权失败，但不影响主登录流程", {
													walletType,
													error: odinFunError,
												});
											}

											// 通知父组件登录成功
											if (onSuccess) {
												onSuccess(principal, address, finalIdentity);
											}

											// 触发VIP状态检测（通过Context自动处理）
											uiLog.info("用户登录成功，VIP状态将自动更新");

										// 2秒后关闭弹窗
										setTimeout(() => {
											setAuthStatus(texts.closing);
											setTimeout(() => {
												onOpenChange(false);
											}, 500);
										}, 1500);
									} catch (err: any) {
										uiLog.error(`❌ ${walletType} 钱包登录错误:`, err);
										setAuthStatus(null);
										if (err.code === 4001) {
											setError(
												language === "zh"
													? "您拒绝了签名请求。"
													: "You rejected the signature request.",
											);
										} else if (err.message?.includes("not found")) {
											setError(
												language === "zh"
													? `未检测到${walletType}钱包，请先安装钱包插件。`
													: `${walletType} wallet not detected, please install the wallet extension first.`,
											);
										} else {
											setError(
												err.message ||
													(language === "zh"
														? `使用${walletType}钱包登录失败，请重试。`
														: `Failed to login with ${walletType} wallet, please try again.`),
											);
										}
										setIsLoading(null);
									}
								})();
							} else if (checkCount >= maxChecks) {
								// 超时处理
								clearInterval(checkInterval);
								if (isLoading === walletType) {
									setError(`请刷新页面后重试，或确保${walletName}已解锁`);
									setIsLoading(null);
									setAuthStatus(null);
								}
							}
						};

						// 监听页面可见性变化
						const handleVisibilityChange = () => {
							if (!document.hidden && isLoading === walletType) {
								uiLog.info("📱 页面重新可见，检查钱包状态...");
								checkWalletAvailable();
							}
						};

						document.addEventListener(
							"visibilitychange",
							handleVisibilityChange,
						);

						// 每秒检查一次钱包是否可用
						checkInterval = setInterval(checkWalletAvailable, 1000);

						// 清理函数
						const cleanup = () => {
							clearInterval(checkInterval);
							document.removeEventListener(
								"visibilitychange",
								handleVisibilityChange,
							);
						};

						// 保存清理函数引用
						cleanupRef.current = cleanup;

						// 20秒后自动清理
						setTimeout(() => {
							cleanup();
							if (cleanupRef.current === cleanup) {
								cleanupRef.current = null;
							}
						}, 20000);
					} else {
						// 其他钱包的原有逻辑
						setTimeout(() => {
							if (isLoading === walletType) {
								setError(
									`如果${walletName}没有自动打开，请手动打开${walletName}并访问此页面完成登录`,
								);
								setIsLoading(null);
								setAuthStatus(null);
							}
						}, 8000);
					}
					return;
				}
			}

			// 直接连接钱包（不需要深链接的情况）
			uiLog.info(`🔗 直接连接 ${walletType} 钱包...`);
			const connectionResult = await connectBitcoinWallet(walletType);
			const wallet = connectionResult.wallet;
			let { address, publicKey } = connectionResult;

			if (!address) {
				setError(
					language === "zh"
						? `未找到${walletType}钱包地址，请先连接钱包。`
						: `${walletType} wallet address not found, please connect wallet first.`,
				);
				setIsLoading(null);
				setAuthStatus(null);
				return;
			}

			uiLog.info(`🔧 SIWB Mode: ${siwbAdapter.getMode()}`);

			const sessionIdentity = Ed25519KeyIdentity.generate();
			const sessionKey = new Uint8Array(sessionIdentity.getPublicKey().toDer());

			setAuthStatus(texts.signing);

			const prepareResponse = await siwbAdapter.prepareLogin(address);
			if ("Err" in prepareResponse) {
				throw new Error(
					language === "zh"
						? `准备登录失败: ${prepareResponse.Err}`
						: `Login preparation failed: ${prepareResponse.Err}`,
				);
			}

			const messageToSign = prepareResponse.Ok;

			const { signature, protocol: usedProtocol, addressType } =
				await signBitcoinMessageWithFallback({
					wallet,
					walletType,
					address,
					message: messageToSign,
					log: (level, message, data) => {
						uiLog[level](message, data);
					},
				});

			uiLog.info("BTC message signed with resolved protocol", {
				walletType,
				address,
				addressType,
				usedProtocol,
			});

			// 获取公钥 - 按照参考代码的方式，在签名后获取
			let actualPublicKey: string | undefined;
			if (walletType === "okx") {
				const connectPublicKey = publicKey;
				let walletPublicKey: string | undefined;
				if (wallet.getPublicKey) {
					walletPublicKey = await wallet.getPublicKey();
				}
				actualPublicKey = walletPublicKey || connectPublicKey;
				if (!actualPublicKey) {
					throw new Error(
						language === "zh"
							? "已检测到 OKX 钱包，但无法读取公钥。请重新连接后重试。"
							: "OKX wallet detected, but public key is unavailable. Please reconnect and try again.",
					);
				}
			} else if (walletType === "phantom") {
				// Phantom钱包公钥处理
				actualPublicKey = normalizeBitcoinPublicKey(address, publicKey);
			} else if (walletType === "xverse") {
				// 根据参考代码，使用client.getPublicKey()方法
				const xverseWallet = wallet as any;
				if (xverseWallet.getPublicKey) {
					actualPublicKey = await xverseWallet.getPublicKey();
				} else {
					// 回退到原来的方式
					actualPublicKey = publicKey;
				}
				actualPublicKey = normalizeBitcoinPublicKey(address, actualPublicKey);
			} else if (walletType === "binance") {
				// Binance 钱包公钥处理
				actualPublicKey = normalizeBitcoinPublicKey(address, publicKey);
			} else if (walletType === "tokenpocket") {
				actualPublicKey = publicKey;
				if (!actualPublicKey && wallet.getPublicKey) {
					actualPublicKey = await wallet.getPublicKey().catch((error) => {
						uiLog.warn("无法从 TokenPocket 当前 provider 获取公钥:", error);
						return undefined;
					});
				}
				actualPublicKey = normalizeBitcoinPublicKey(address, actualPublicKey);
			} else {
				actualPublicKey = normalizeBitcoinPublicKey(address, publicKey);
			}

			setAuthStatus(texts.verifying);

			const signMessageType = buildSiwbSignMessageType(usedProtocol);

			// 使用适配器执行登录
			const loginResponse = await siwbAdapter.login(
				signature,
				address,
				actualPublicKey,
				sessionKey,
				signMessageType,
			);

			if ("Err" in loginResponse) {
				throw new Error(
					language === "zh"
						? `登录失败: ${loginResponse.Err}`
						: `Login failed: ${loginResponse.Err}`,
				);
			}

			// 从登录响应中获取信息
			const loginDetails = loginResponse.Ok;

			// 使用适配器获取委托
			const delegationResponse = await siwbAdapter.getDelegation(
				address,
				sessionKey,
				loginDetails.expiration,
			);

			if ("Err" in delegationResponse) {
				throw new Error(
					language === "zh"
						? `获取委托失败: ${delegationResponse.Err}`
						: `Failed to get delegation: ${delegationResponse.Err}`,
				);
			}

			// 根据委托链创建最终身份
			const delegationResult = delegationResponse.Ok;
			const userCanisterPubkeyDer = new Uint8Array(
				loginDetails.user_canister_pubkey,
			);

			// 根据 get_delegation 结果创建委托链
			function createDelegationChainFromGetResult(
				getResult: any,
				middlePubkeyDer: Uint8Array,
			) {
				const delegations = [
					{
						delegation: new Delegation(
							getResult.delegation.pubkey,
							getResult.delegation.expiration,
							getResult.delegation.targets
								? getResult.delegation.targets[0]
								: undefined,
						),
						signature: getResult.signature,
					},
				];
				// 使用传入的 middlePubkeyDer 构建委托链
				return DelegationChain.fromDelegations(
					delegations,
					middlePubkeyDer,
				);
			}

			const delegationChain = createDelegationChainFromGetResult(
				delegationResult,
				userCanisterPubkeyDer,
			);
			const finalIdentity = DelegationIdentity.fromDelegation(
				sessionIdentity,
				delegationChain,
			);

			// 获取 Principal
			const principal = finalIdentity.getPrincipal().toString();

			// 保存到本地存储
			localStorage.setItem("siwb_authenticated", "true");
			localStorage.setItem("user_btc_address", address);
			localStorage.setItem("user_principal", principal);
			localStorage.setItem("user_wallet_type", walletType); // 保存钱包类型
			// 保存公钥用于后续 Fomowell 关联等操作
			if (actualPublicKey) {
				localStorage.setItem("user_btc_publicKey", actualPublicKey);
			}

			// 保存重建身份所需的数据，使用与 siwb.ts 一致的数据结构
			localStorage.setItem(
				"user_session_key",
				Buffer.from(sessionKey).toString("hex"),
			);
			// 使用加密存储会话私钥
			await SessionKeyEncryption.encryptSessionKey(
				Buffer.from(sessionIdentity.getKeyPair().secretKey).toString("hex"),
				principal,
			);

			// 构建与 siwb.ts 一致的委托数据结构
			const delegationWithMeta = {
				delegationData: delegationResult,
				userCanisterPubkey: Array.from(userCanisterPubkeyDer), // 保存为数组便于序列化
			};

			// 使用自定义序列化函数处理 BigInt
			localStorage.setItem(
				"user_delegation_result",
				JSON.stringify(delegationWithMeta, (key, value) =>
					typeof value === "bigint" ? value.toString() : value,
				),
			);

			// 显示成功状态
			setAuthStatus(texts.success);
			setLoginSuccess(true);
			setError(null);
			metrics.mark("login_ui_completed", { principal });

				try {
					await OdinFunAuthService.getOdinFunAuth(finalIdentity);
					metrics.mark("odin_fun_auth_completed");
				} catch (odinFunError: any) {
					uiLog.warn("odin.fun 授权失败，但不影响主登录流程", {
						walletType,
						error: odinFunError,
					});
				}

				// 通知父组件登录成功
				if (onSuccess) {
					onSuccess(principal, address, finalIdentity);
				}

				// 触发VIP状态检测（通过Context自动处理）
				uiLog.info("用户登录成功，VIP状态将自动更新");

			// 2秒后关闭弹窗
			setTimeout(() => {
				setAuthStatus(texts.closing);
				setTimeout(() => {
					onOpenChange(false);
				}, 500);
			}, 1500);
		} catch (err: any) {
			uiLog.error(`❌ ${walletType} 钱包登录错误:`, err);
			if (walletType === "tokenpocket") {
				clearStoredWalletLoginIntent();
				autoStartedWalletRef.current = null;
			}
			setAuthStatus(null);
			if (err.code === 4001) {
				setError(
					language === "zh"
						? "您拒绝了签名请求。"
						: "You rejected the signature request.",
				);
			} else if (
				walletType === "okx" &&
				(err.message?.includes(OKX_CHROME_EXTENSION_URL) ||
					err.message?.includes("OKX Wallet Chrome extension") ||
					err.message?.includes("未检测到 OKX"))
			) {
				setError(
					language === "zh"
						? `未检测到 OKX Wallet Chrome 插件。请安装插件、确认插件已解锁，并在安装后刷新页面：${OKX_CHROME_EXTENSION_URL}`
						: `OKX Wallet Chrome extension not detected. Install it, make sure it is unlocked, and refresh the page: ${OKX_CHROME_EXTENSION_URL}`,
				);
			} else if (
				walletType === "okx" &&
				!isMobileDevice() &&
				isOkxConnectedAddressMismatchError(err)
			) {
				setError(
					language === "zh"
						? "检测到 OKX 当前签名账户与本站已连接账户不一致。请打开 OKX 扩展，断开 www.odin.baby 的站点授权，切换到要登录的 BTC 账户后重新连接。"
						: "The current OKX signing account does not match the account connected to this site. Open the OKX extension, disconnect www.odin.baby site authorization, switch to the BTC account you want to use, and reconnect.",
				);
			} else if (err.message?.includes("not found")) {
				setError(
					language === "zh"
						? `未检测到${walletType}钱包，请先安装钱包插件。`
						: `${walletType} wallet not detected, please install the wallet extension first.`,
				);
			} else {
				setError(
					err.message ||
						(language === "zh"
							? `使用${walletType}钱包登录失败，请重试。`
							: `Failed to login with ${walletType} wallet, please try again.`),
				);
			}
		} finally {
			if (!loginSuccess) {
				setIsLoading(null);
			}
		}
	};

	autoResumeWalletLoginRef.current = (walletType: string) => {
		void handleBitcoinWalletSignIn(walletType);
	};

	const handleDialogOpenChange = (nextOpen: boolean) => {
		if (!nextOpen && getStoredWalletLoginIntent()?.walletType === "tokenpocket") {
			clearStoredWalletLoginIntent();
			autoStartedWalletRef.current = null;
		}
		onOpenChange(nextOpen);
	};

	// Reset error when modal opens and cleanup when closes
	React.useEffect(() => {
		if (open) {
			setError(null);
			setIsLoading(null);
			setAuthStatus(null);
			setLoginSuccess(false);
		} else {
			// 模态框关闭时清理
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		}
	}, [open]);

	// 组件卸载时清理
	React.useEffect(() => {
		return () => {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, []);

	return (
		<Dialog open={open} onOpenChange={handleDialogOpenChange}>
			<DialogContent className="max-w-[90vw] w-full sm:max-w-md rounded-2xl bg-light-surface dark:bg-dark-surface shadow-md mx-4">
				<DialogHeader>
					<DialogTitle className="text-center text-lg sm:text-xl font-bold text-light-primary dark:text-dark-primary">
						{texts.title}
					</DialogTitle>
					<DialogDescription className="text-center text-sm sm:text-base text-light-text/70 dark:text-dark-text/70">
						{texts.description}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-3 sm:space-y-4 sm:py-4">
					{error && (
						<div className="p-2 sm:p-3 text-xs sm:text-sm text-status-error bg-status-error/10 dark:bg-status-error/20 rounded-2xl border border-status-error/20 dark:border-status-error/40">
							{error}
						</div>
					)}

					{authStatus && !error && (
						<div className="p-2 sm:p-3 text-xs sm:text-sm text-status-info bg-status-info/10 dark:bg-status-info/20 rounded-2xl border border-status-info/20 dark:border-status-info/40 flex items-center gap-2">
							{loginSuccess ? (
								<CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
							) : (
								<Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
							)}
							{authStatus}
						</div>
					)}

					<div className="space-y-2 sm:space-y-3">
						{/* Bitcoin钱包选项 */}
						<div className="space-y-1 sm:space-y-2">
							<p className="text-xs sm:text-sm font-medium text-center text-light-text/70 dark:text-dark-text/70">
								{texts.btcWallets}
							</p>
							<div className="space-y-1 sm:space-y-2">
								{supportedWallets.map((wallet) => {
									const isAvailable = availableWallets.includes(wallet.id);
									const mobileEnv = getMobileWalletEnvironment();

									// Binance 钱包特殊处理：Chrome 扩展不支持 Bitcoin
									const isBinanceExtOnly =
										wallet.id === "binance" &&
										isBinanceExtensionInstalled() &&
										!isBinanceBitcoinSupported();

									// 移动端特殊逻辑：如果在钱包浏览器中，检查是否为对应钱包
									let showNotInstalled = false;
									let showBinanceAppHint = false;
									const showTokenPocketOpenHint =
										wallet.id === "tokenpocket" &&
										mobileEnv.isMobile &&
										mobileEnv.walletType !== "tokenpocket";

									if (wallet.id === "binance" && isBinanceExtOnly) {
										// Binance Chrome 扩展已安装但不支持 Bitcoin
										showBinanceAppHint = true;
									} else if (!mobileEnv.isMobile) {
										// PC端：显示真实的安装状态
										showNotInstalled = !isAvailable;
									} else {
										// 移动端：都不显示"未安装"，因为可以通过深链接跳转
										showNotInstalled = false;
									}

									return (
										<Button
											key={wallet.id}
											onClick={() => {
												if (
													wallet.id === "okx" &&
													!mobileEnv.isMobile &&
													!isAvailable
												) {
													setError(
														language === "zh"
															? `未检测到 OKX Wallet Chrome 插件。请先安装插件并刷新页面：${OKX_CHROME_EXTENSION_URL}`
															: `OKX Wallet Chrome extension not detected. Install it and refresh the page: ${OKX_CHROME_EXTENSION_URL}`,
													);
													return;
												}
												if (isBinanceExtOnly) {
													// 显示提示而不是尝试连接
													setError(
														language === "zh"
															? "Binance Wallet Chrome 扩展暂不支持 Bitcoin，请使用 Binance App 打开本页面"
															: "Binance Wallet Chrome extension does not support Bitcoin yet. Please use Binance App.",
													);
													return;
												}
												handleBitcoinWalletSignIn(wallet.id);
											}}
											disabled={isLoading !== null}
											variant="outline"
											className="w-full h-10 sm:h-12 text-sm sm:text-base relative rounded-2xl border-light-border dark:border-dark-border hover:bg-light-primary/10 dark:hover:bg-dark-primary/10 hover:text-light-primary dark:hover:text-dark-primary hover:border-light-primary dark:hover:border-dark-primary shadow-sm"
										>
											{isLoading === wallet.id ? (
												<Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
											) : (
												<div className="mr-2 h-4 w-4 sm:h-5 sm:w-5 relative">
													<Image
														src={wallet.icon}
														alt={wallet.name}
														width={16}
														height={16}
														className="object-contain sm:w-5 sm:h-5"
													/>
												</div>
											)}
											<span className="flex-1 text-left">{wallet.name}</span>
											{showBinanceAppHint && (
												<span className="text-xs sm:text-xs text-amber-600 dark:text-amber-400">
													{language === "zh"
														? "请使用 Binance App"
														: "Use Binance App"}
												</span>
											)}
											{showTokenPocketOpenHint && (
												<span className="text-xs sm:text-xs text-amber-600 dark:text-amber-400">
													{language === "zh"
														? "前往 TP 打开"
														: "Open in TP"}
												</span>
											)}
											{showNotInstalled && (
												<span className="text-xs sm:text-xs text-light-text/50 dark:text-dark-text/50">
													{language === "zh" ? "未安装" : "Not Installed"}
												</span>
											)}
											{/* 当前钱包提示：在对应钱包 App 中或检测到支持 Bitcoin 的 Binance */}
											{(mobileEnv.isMobile &&
												mobileEnv.walletType === wallet.id) ||
											(wallet.id === "binance" &&
												isBinanceBitcoinSupported()) ? (
												<span className="text-xs sm:text-xs text-green-600 dark:text-green-400">
													{language === "zh" ? "当前钱包" : "Current Wallet"}
												</span>
											) : null}
										</Button>
									);
								})}
							</div>
						</div>
					</div>

					{!getMobileWalletEnvironment().isMobile &&
						!availableWallets.includes("okx") && (
							<div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 p-3 sm:p-4">
								<p className="text-sm font-medium text-amber-700 dark:text-amber-300">
									{texts.okxInstallTitle}
								</p>
								<p className="mt-1 text-xs leading-relaxed text-amber-700/80 dark:text-amber-200/80">
									{texts.okxInstallDescription}
								</p>
								<div className="mt-3 flex flex-col gap-2 sm:flex-row">
									<Button
										type="button"
										onClick={() => {
											window.open(
												OKX_CHROME_EXTENSION_URL,
												"_blank",
												"noopener,noreferrer",
											);
										}}
										className="h-10 rounded-2xl bg-amber-600 text-white hover:bg-amber-700"
									>
										{texts.okxInstallButton}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={() => {
											void refreshWalletAvailabilityRef.current?.();
										}}
										className="h-10 rounded-2xl border-amber-500/30 bg-transparent text-amber-700 hover:bg-amber-500/10 dark:text-amber-200"
									>
										{texts.recheckWallets}
									</Button>
								</div>
							</div>
						)}

					<div className="pt-3 sm:pt-4">
						<Button
							onClick={() => onOpenChange(false)}
							variant="ghost"
							className="w-full h-8 sm:h-10 text-sm sm:text-base text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-2xl"
						>
							{texts.cancel}
						</Button>
					</div>
				</div>

				<div className="text-center px-2">
					<p className="text-xs sm:text-xs text-light-text/60 dark:text-dark-text/60 leading-relaxed">
						{texts.agreement}
					</p>
				</div>
			</DialogContent>
		</Dialog>
	);
}
